import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import LogoutDialog from "../../Login/LogoutDialog";
import constant from "../../../Constant";
import styles from "./sidebar.module.scss";
import Sidebar_Logo from "../../../Assets/wone_sidebar_logo.svg";
import LanguageMenu from "components/language-menu/language-menu";
import {
  useAuth,
  checkUserHasRole,
  WONE_ADMIN_ROLE,
} from "../../../providers/auth";
import { ComponentType } from "react";
import clsx from "clsx";
import { trackEvent } from "utility/mixpanel";
import { setOriEntryPoint } from "apps/user/learner/lib/ori-starter-message";
import { useTranslation, TranslateFunction } from "lib/i18n/use-translation";

const ORI_NAV_PATH = "/user/chat";

/**
 * `item.title` is the canonical English nav id — it drives the Mixpanel event
 * name and the Ori branch below — so it must never become localised copy.
 * The visible label is looked up from it instead. Literal `t()` calls (not a
 * `t(item.labelKey)` indirection) so the extractor can see the keys.
 */
const navLabel = (t: TranslateFunction, title: string): string => {
  const labels: Record<string, string> = {
    Home: t("components:nav.home"),
    Track: t("components:nav.track"),
    Discover: t("components:nav.discover"),
    Ori: t("components:nav.ori"),
    Profile: t("components:nav.profile"),
  };
  return labels[title] ?? title;
};

const woneAdminUrl = (): string => {
  const url = process.env.REACT_APP_ADMIN_URL;
  if (!url) throw new Error("REACT_APP_ADMIN_URL is not set for this build");
  return url;
};

export type SideBarProps = {
  sidebarData: [];
};

interface SidebarNavItemProps {
  title: string;
  path: string;
  imgName: ComponentType<any>; // Changed from string to ComponentType
}
const SideBar: React.FC = ({ sidebarData }: SideBarProps) => {
  const { t } = useTranslation(["components", "common"]);
  const [logoutDialog, setLogoutDialog] = useState<boolean>(false);
  const { logout, user, companyAdmin, learner, chooseLanguage } = useAuth();

  const logOut = () => {
    setLogoutDialog(true);
  };
  const closeLogoutDialog = () => {
    setLogoutDialog(false);
  };
  const closeAfterYes = () => {
    logout();
    setLogoutDialog(false);
  };

  return (
    <div className={styles["sidebar-container"]}>
      <div className={styles["navigation-container"]}>
        <Link to="/">
          <img
            src={Sidebar_Logo}
            className={styles["sidebar-logo"]}
            alt="walk-on-earth-logo"
            width="82px"
            height="20px"
          />
        </Link>

        <ul>
          {sidebarData.map((item: SidebarNavItemProps, index) => {
            const label = navLabel(t, item.title);
            return (
              <li key={index}>
                <NavLink
                  title={label}
                  className={({ isActive }) =>
                    clsx(styles.link, isActive && styles["active-link"])
                  }
                  to={item.path}
                  onClick={() => {
                    trackEvent(`${item.title} Nav Clicked`);
                    if (item.path === ORI_NAV_PATH || item.title === "Ori") {
                      setOriEntryPoint("nav_tab");
                    }
                  }}
                >
                  <item.imgName
                    className={styles["nav-icon"]}
                    width={23}
                    height={23}
                  />
                  <span>{label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
      <div className={styles["footer-nav-container"]}>
        <div className={styles["profile"]}>
          <LanguageMenu
            variant="accordion"
            triggerClassName={styles.link}
            itemClassName={styles.link}
            profileLanguage={learner?.language}
            onChoose={chooseLanguage}
          />
        </div>

        {checkUserHasRole(user, WONE_ADMIN_ROLE) && (
          <div className={styles["profile"]}>
            <a
              href={woneAdminUrl()}
              className={styles.link}
              onClick={() => trackEvent("Switch to WONE Admin Clicked")}
            >
              <span>{t("components:sidebar.switchToWoneAdmin")}</span>
            </a>
          </div>
        )}

        {companyAdmin && (
          <div className={styles["profile"]}>
            <Link
              to="/client"
              className={styles.link}
              onClick={() => trackEvent("Switch to Admin Clicked")}
            >
              <span>{t("components:sidebar.switchToAdmin")}</span>
            </Link>
          </div>
        )}

        <a
          href={constant.PRIVACY_POLICY_URL}
          className={styles.link}
          onClick={() => trackEvent("Privacy Terms Clicked")}
        >
          {t("common:legal.privacy")}
        </a>

        <div
          className={styles.link}
          onClick={() => {
            trackEvent("Log Out Clicked");
            logOut();
          }}
        >
          <span>{t("common:action.logOut")}</span>
        </div>
      </div>
      {logoutDialog && (
        <LogoutDialog
          closeLogoutDialog={closeLogoutDialog}
          closeAfterYes={closeAfterYes}
        />
      )}
    </div>
  );
};
export default SideBar;
